import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Box, Typography, Select, Option, Avatar } from "@mui/joy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HistoryIcon from "@mui/icons-material/History";
import EventNote from "@mui/icons-material/EventNote";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ModalContext";
import { Class, ClassStatus, calculateClassStatus, EnrollmentType } from "../../types/Class";
import { Student as DBStudent } from "../../types/Student";
import { GradeLevelLabels } from "../../types";
import { fetchProfilePicture, getInitials, formatStudentName } from "../../utils";
import { formatScheduleDisplay, getEnrollmentTypeDisplay } from "../../utils/schedule-formatting";

type StudentWithProfilePicture = DBStudent & { profile_picture?: string | null };

// Validation schema for enrollment
const enrollmentSchema = yup.object().shape({
  class_id: yup.string().required("Class selection is required"),
  student_id: yup.string().required("Student selection is required"),
  enrollment_type: yup.string().oneOf([EnrollmentType.Full, EnrollmentType.Half]).optional(),
});

interface EnrollmentFormValues {
  class_id: string;
  student_id: string;
  enrollment_type?: EnrollmentType;
}

interface EnrollStudentModalProps {
  classId?: string;
  studentId?: string;
  onSuccess?: () => void;
  onEnrollmentUpdated?: () => void | Promise<void>;
}

const EnrollStudentModal: React.FC<EnrollStudentModalProps> = ({ classId, studentId, onSuccess, onEnrollmentUpdated }) => {
  const { closeModal, setModalTitle, registerSubmitCallback } = useModal();
  const { showToast } = useToast();
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<StudentWithProfilePicture[]>([]);
  const [enrolledClassIds, setEnrolledClassIds] = useState<Set<string>>(new Set());
  const [enrolledStudentIds, setEnrolledStudentIds] = useState<Set<string>>(new Set());
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);

  const defaultValues = {
    class_id: classId || "",
    student_id: studentId || "",
    enrollment_type: EnrollmentType.Full,
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EnrollmentFormValues>({
    resolver: yupResolver(enrollmentSchema),
    defaultValues,
    mode: "onTouched", // Validate only after a field is touched
    reValidateMode: "onBlur", // Re-validate when the field loses focus
  });

  const onSubmit = useCallback(async (formData: EnrollmentFormValues) => {
    try {
      const { error } = await supabase
        .from("classes_students")
        .insert([{ 
          class_id: classId || formData.class_id, 
          student_id: studentId || formData.student_id,
          enrollment_type: formData.enrollment_type || "full"
        }]);

      if (error) throw error;

      showToast("Student enrolled successfully!", "success", "solid");
      closeModal();
      if (onSuccess) {
        onSuccess();
      }
      if (onEnrollmentUpdated) {
        await onEnrollmentUpdated();
      }
    } catch (error) {
      showToast("Error enrolling student.", "danger", "solid");
    }
  }, [classId, studentId, showToast, closeModal, onSuccess, onEnrollmentUpdated]);

  useEffect(() => {
    setModalTitle(classId ? "Enroll Student in Class" : "Enroll Student in Classes");
    registerSubmitCallback(async () => {
      const result = handleSubmit(onSubmit);
      await result();
    });
  }, [setModalTitle, handleSubmit, onSubmit, registerSubmitCallback, classId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (classId) {
          // Fetch students for a specific class
          const { data: allStudents, error: studentsError } = await supabase
            .from("students")
            .select("*")
            .order("first_name");

          if (studentsError) throw studentsError;

          const { data: enrolledStudents, error: enrolledError } = await supabase
            .from("classes_students")
            .select("student_id")
            .eq("class_id", classId);

          if (enrolledError) throw enrolledError;

          // Also fetch the class details to check if it supports half-day
          const { data: classDetails, error: classError } = await supabase
            .from("classes_view")
            .select("*")
            .eq("id", classId)
            .single();

          if (classError) throw classError;

          const studentsWithPictures = await Promise.all(
            allStudents.map(async (student) => {
              return {
                ...student,
                profile_picture: await fetchProfilePicture("students", student.profile_picture_id),
              } as StudentWithProfilePicture;
            })
          );

          const enrolledIds = new Set(enrolledStudents.map(es => es.student_id));
          setEnrolledStudentIds(enrolledIds);
          setStudents(studentsWithPictures);
          setClasses([classDetails]);
        } else if (studentId) {
          // Fetch classes for a specific student
          const { data: availableClasses, error: availableError } = await supabase
            .from("classes_view")
            .select("*, primary_teacher:primary_teacher_id (*)")
            .order("start_year", { ascending: false })
            .order("start_month", { ascending: false });

          if (availableError) throw availableError;

          const { data: enrolledClasses, error: enrolledError } = await supabase
            .from("classes_students")
            .select("class_id")
            .eq("student_id", studentId);

          if (enrolledError) throw enrolledError;

          const newEnrolledClassIds = new Set(enrolledClasses.map((ec) => ec.class_id));
          setEnrolledClassIds(newEnrolledClassIds);
          setClasses(availableClasses);
        }
      } catch (error) {
        showToast("Error fetching data.", "danger", "solid");
      }
    };

    fetchData();
  }, [classId, studentId, showToast]);



  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%", position: "relative" }}>
      <Controller
        name={classId ? "student_id" : "class_id"}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            value={field.value || ""}
            onChange={(_, newValue) => {
              field.onChange(newValue);
              if (!classId && newValue) {
                const selectedClassData = classes.find(c => c.id === newValue);
                setSelectedClass(selectedClassData || null);
              }
            }}
            placeholder={classId ? "Select a student..." : "Select a class..."}
            sx={{ height: "74px" }}
            renderValue={(option) => {
              if (!option) return null;
              
              if (classId) {
                // For student selection, show just the name
                const student = students.find(s => s.id === option.value);
                if (student) {
                  return `${formatStudentName(student)} (Grade: ${GradeLevelLabels[student.grade_level as import("../../types/Grade").GradeLevel] || "Not Set"})`;
                }
              } else {
                // For class selection, show the class info
                const cls = classes.find(c => c.id === option.value);
                if (cls) {
                  const status = calculateClassStatus(cls);
                  const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);
                  return `${cls.start_month}/${cls.start_year} — ${cls.end_month}/${cls.end_year} (${formattedStatus})`;
                }
              }
              return option.label;
            }}
          >
            {classId ? (
              // Show student options when enrolling in a specific class
              students.map((student) => {
                const isEnrolled = enrolledStudentIds.has(student.id);
                return (
                  <Option
                    key={student.id}
                    value={student.id}
                    disabled={isEnrolled}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: isEnrolled ? "text.disabled" : "text.primary",
                      opacity: isEnrolled ? 0.6 : 1,
                      "& .MuiTypography-root": {
                        opacity: isEnrolled ? 0.6 : 1,
                      },
                    }}
                  >
                    <Avatar
                      src={student.profile_picture || undefined}
                      sx={{ 
                        width: 32, 
                        height: 32,
                        opacity: isEnrolled ? 0.6 : 1,
                      }}
                    >
                      {!student.profile_picture ? getInitials(student) : null}
                    </Avatar>
                    <Box>
                      <Typography>
                        {formatStudentName(student)}
                        {isEnrolled ? " — Already Enrolled" : ""}
                      </Typography>
                      <Typography level="body-sm">
                        Grade: {GradeLevelLabels[student.grade_level as import("../../types/Grade").GradeLevel] || "Not Set"}
                      </Typography>
                    </Box>
                  </Option>
                );
              })
            ) : (
              // Show class options when enrolling a specific student
              classes.map((cls) => {
                const isEnrolled = enrolledClassIds.has(cls.id);
                const primaryTeacher = cls.primary_teacher
                  ? `${cls.primary_teacher.first_name} ${cls.primary_teacher.last_name}`
                  : "No Teacher Assigned";
                const formattedSchedule = formatScheduleDisplay(cls.schedule);
                const status = calculateClassStatus(cls);
                const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);

                const statusIcon =
                  status === ClassStatus.Future ? (
                    <EventAvailableIcon color="primary" fontSize="small" />
                  ) : status === ClassStatus.Past ? (
                    <HistoryIcon color="warning" fontSize="small" />
                  ) : (
                    <EventNote color="success" fontSize="small" />
                  );

                return (
                  <Option
                    key={cls.id}
                    value={cls.id}
                    disabled={isEnrolled}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      color: isEnrolled ? "text.disabled" : "text.primary",
                      "& .MuiTypography-root": {
                        opacity: isEnrolled ? 0.6 : 1,
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} sx={{ width: "100%" }}>
                      {statusIcon}
                      <Box>
                        <Typography fontWeight="bold">
                          {cls.start_month}/{cls.start_year} — {cls.end_month}/{cls.end_year} ({formattedStatus})
                          {isEnrolled ? " — Enrolled" : ""}
                        </Typography>
                        <Typography fontSize="sm">Teacher: {primaryTeacher}</Typography>
                        <Typography fontSize="sm">Schedule: {formattedSchedule}</Typography>
                      </Box>
                    </Box>
                  </Option>
                );
              })
            )}
          </Select>
        )}
      />
      {errors[classId ? "student_id" : "class_id"] && (
        <Typography sx={{ color: "red", mt: 1 }}>
          {classId ? "Please select a student" : "Please select a class"}
        </Typography>
      )}

      {(() => {
        const targetClass = !classId ? selectedClass : classes.find(c => c.id === classId);
        const hasAnyHalfDaySupport = targetClass?.schedule && Object.values(targetClass.schedule).some((day: any) => day.supportsHalf);
        
        if (!hasAnyHalfDaySupport) return null;
        
        return (
          <Box sx={{ mt: 3 }}>
            <Typography>Enrollment Type</Typography>
            <Controller
              control={control}
              name="enrollment_type"
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  placeholder="Select enrollment type..."
                >
                  <Option value={EnrollmentType.Full}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>{getEnrollmentTypeDisplay('full', targetClass?.schedule)}</Typography>
                    </Box>
                  </Option>
                  <Option value={EnrollmentType.Half}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography>{getEnrollmentTypeDisplay('half', targetClass?.schedule)}</Typography>
                    </Box>
                  </Option>
                </Select>
              )}
            />
            {errors.enrollment_type && (
              <Typography sx={{ color: "red", mt: 1 }}>
                {errors.enrollment_type.message}
              </Typography>
            )}
          </Box>
        );
      })()}
    </Box>
  );
};

export default EnrollStudentModal;
